/**
 * The calm lower half of the job page (#1717, Finish Line slice 3): the words
 * and the order. Pure functions, each with a test (`calmModel.test.ts`).
 *
 * Nothing here decides anything new. A device's word comes from the same facts
 * the Results card reads (its latest test, its repair, its disposition, the
 * server's `resolved` flag); a filing word comes from the Finish Line engine's
 * own "filing" promise.
 */
import type { FeedEntry } from "../../jobDetailHelpers";
import type { JobPromise } from "@/lib/finish-line";
import type { SendHistoryRecipient } from "@/lib/use-send-history";
import { isImageFile } from "../paperwork-rows";
import type { JobAssetRow, JobRepairResult, JobTestResult, ReportAttachment } from "../types";
import { TEST_STATE_LABELS, testState } from "@/lib/test-state";

// ── Devices ─────────────────────────────────────────────────────────────────

/** The colour a status word is printed in (never a chip). */
export type DeviceTone = "pass" | "fail" | "warn" | "todo";

/** Which group a row lands in. "look" rows come first and start open. */
export type DeviceGroupKey = "look" | "pass" | "done" | "todo";

export interface CalmDevice {
    asset: JobAssetRow;
    /** Its own latest test on this job, if any. */
    test?: JobTestResult;
    /** A bypass leg's result lives on its main's test. */
    inheritedTest?: JobTestResult;
    repair?: JobRepairResult;
    word: string;
    tone: DeviceTone;
    group: DeviceGroupKey;
    /** For the header count: "18 passed · 1 incomplete · 3 not tested". */
    tally: "passed" | "failed" | "incomplete" | "not tested" | "repaired" | "replaced" | "to test";
}

/** Latest test per device (the Results card's rule: last performed_at wins). */
export function latestTestByAsset(tests: JobTestResult[]): Map<string, JobTestResult> {
    const by = new Map<string, JobTestResult>();
    for (const t of tests) {
        const prev = by.get(t.asset_id);
        if (!prev || new Date(t.performed_at).getTime() > new Date(prev.performed_at).getTime()) by.set(t.asset_id, t);
    }
    return by;
}

const lower = (s: string | null | undefined) => (s ?? "").toLowerCase();

/**
 * One device's status word, its colour and its group.
 *
 * Needs a look (first, open): failed, incomplete, couldn't test, not at this
 * site, replaced or retired on the site record, and — after the visit — a
 * device with no result at all. Fine (folded when there are more than 3):
 * passed, repaired or replaced as planned, and before the visit, to test.
 */
export function classifyDevice(
    asset: JobAssetRow,
    test: JobTestResult | undefined,
    inheritedTest: JobTestResult | undefined,
    hasRepair: boolean,
    postVisit: boolean,
): Pick<CalmDevice, "word" | "tone" | "group" | "tally"> {
    const disposition = lower(asset.disposition);
    if (disposition === "not_located")
        return { word: "Can't find it", tone: "warn", group: "look", tally: "not tested" };
    if (disposition) return { word: "Couldn't test", tone: "warn", group: "look", tally: "not tested" };
    const status = lower(asset.asset_status);
    if (status === "replaced" || status === "retired") {
        return {
            word: status === "replaced" ? "Replaced" : "Retired",
            tone: "warn",
            group: "look",
            tally: "not tested",
        };
    }
    const result = test ?? inheritedTest;
    // One result per leg (trust audit Phase 1): a bypass reads its own leg,
    // and a detector's main reads its own, never the other's.
    const legResult = test
        ? test.bypass_leg_result != null
            ? test.main_leg_result
            : null
        : (inheritedTest?.bypass_leg_result ?? null);
    const overall = lower(legResult ?? result?.overall_result);
    if (overall === "fail") return { word: "Failed", tone: "fail", group: "look", tally: "failed" };
    if (overall === "incomplete") return { word: "Incomplete", tone: "fail", group: "look", tally: "incomplete" };
    // One rule (lib/test-state.ts): couldn't test and result-not-known are
    // never a pass, and never "recorded".
    if (testState(overall) === "cant_test")
        return { word: "Couldn't test", tone: "warn", group: "look", tally: "not tested" };
    if (testState(overall) === "unknown")
        return { word: TEST_STATE_LABELS.unknown, tone: "warn", group: "look", tally: "not tested" };
    const action = lower(asset.required_action) || "test";
    if (action === "replace") {
        if (asset.resolved) return { word: "✓ Replaced", tone: "pass", group: "done", tally: "replaced" };
        return postVisit
            ? { word: "No result", tone: "warn", group: "look", tally: "not tested" }
            : { word: "To replace", tone: "todo", group: "todo", tally: "to test" };
    }
    if (action === "repair" && !hasRepair) {
        return postVisit
            ? { word: "Repair pending", tone: "warn", group: "look", tally: "not tested" }
            : { word: "To repair", tone: "todo", group: "todo", tally: "to test" };
    }
    if (overall === "pass") return { word: "✓ Passed", tone: "pass", group: "pass", tally: "passed" };
    if (action === "repair" && hasRepair) return { word: "✓ Repaired", tone: "pass", group: "done", tally: "repaired" };
    if (result) return { word: "Recorded", tone: "pass", group: "done", tally: "passed" };
    return postVisit
        ? { word: "No result", tone: "warn", group: "look", tally: "not tested" }
        : { word: "To test", tone: "todo", group: "todo", tally: "to test" };
}

/** Every linked device as a calm row, in the page's order. */
export function buildDevices(
    assets: JobAssetRow[],
    tests: JobTestResult[],
    repairs: JobRepairResult[],
    postVisit: boolean,
): CalmDevice[] {
    const testBy = latestTestByAsset(tests);
    const repairBy = new Map<string, JobRepairResult>();
    for (const r of repairs) repairBy.set(r.asset_id, r); // ordered by created_at, last wins
    return assets.map((asset) => {
        const test = testBy.get(asset.asset_id);
        const inheritedTest = !test && asset.parent_asset_id ? testBy.get(asset.parent_asset_id) : undefined;
        const repair = repairBy.get(asset.asset_id);
        return {
            asset,
            test,
            inheritedTest,
            repair,
            ...classifyDevice(asset, test, inheritedTest, !!repair, postVisit),
        };
    });
}

const LOOK_ORDER: Record<string, number> = { fail: 0, warn: 1, pass: 2, todo: 3 };

export interface DeviceGroup {
    key: Exclude<DeviceGroupKey, "look">;
    label: string;
    rows: CalmDevice[];
    /** More than 3 rows: the group starts as one line. */
    folds: boolean;
}

export interface DeviceSections {
    look: CalmDevice[];
    groups: DeviceGroup[];
}

const GROUP_LABEL: Record<DeviceGroup["key"], string> = {
    pass: "✓ Passed",
    done: "✓ Done",
    todo: "To test",
};

/** Exceptions first (red before amber, then the page's order); then the fine
 *  groups, each folded to one line when it holds more than 3 devices. */
export function sectionDevices(devices: CalmDevice[]): DeviceSections {
    const look = devices
        .map((d, i) => ({ d, i }))
        .filter((x) => x.d.group === "look")
        .sort((a, b) => LOOK_ORDER[a.d.tone]! - LOOK_ORDER[b.d.tone]! || a.i - b.i)
        .map((x) => x.d);
    const groups: DeviceGroup[] = [];
    for (const key of ["pass", "done", "todo"] as const) {
        const rows = devices.filter((d) => d.group === key);
        if (rows.length > 0) groups.push({ key, label: GROUP_LABEL[key], rows, folds: rows.length > 3 });
    }
    return { look, groups };
}

const TALLY_ORDER: CalmDevice["tally"][] = [
    "passed",
    "repaired",
    "replaced",
    "failed",
    "incomplete",
    "not tested",
    "to test",
];

/** "18 passed · 1 incomplete · 3 not tested". */
export function deviceSummary(devices: CalmDevice[]): string {
    const counts = new Map<string, number>();
    for (const d of devices) counts.set(d.tally, (counts.get(d.tally) ?? 0) + 1);
    return TALLY_ORDER.filter((t) => counts.has(t))
        .map((t) => `${counts.get(t)} ${t}`)
        .join(" · ");
}

/** "9:59 AM" in the viewer's clock. */
export function clockTime(iso: string | null | undefined): string | null {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** The folded group's one line: "18 devices · tested 9:59 AM–12:36 PM". */
export function foldLine(group: DeviceGroup): string {
    const n = `${group.rows.length} devices`;
    if (group.key !== "pass") return n;
    const times = group.rows
        .map((d) => (d.test ?? d.inheritedTest)?.performed_at)
        .filter((t): t is string => !!t)
        .sort();
    if (times.length === 0) return n;
    const first = clockTime(times[0]);
    const last = clockTime(times[times.length - 1]);
    return `${n} · tested ${first === last ? first : `${first}–${last}`}`;
}

/** "when · who" on a device row. */
export function whenWho(d: CalmDevice): string {
    const t = d.test ?? d.inheritedTest;
    const when = clockTime(t?.performed_at ?? d.repair?.created_at);
    const who = (t?.performed_by_name || t?.tester_name || d.repair?.performed_by_name || "").split(" ")[0];
    return [when, who].filter(Boolean).join(" · ");
}

/** A serial that is really the model name (Benbrook "375XL" on a Wilkins
 *  375XL) — the engine's truth check #1, said on the row too. */
export function serialIsModel(asset: Pick<JobAssetRow, "serial_number" | "model">): boolean {
    const s = (asset.serial_number ?? "").trim().toUpperCase();
    const m = (asset.model ?? "").trim().toUpperCase();
    return s.length >= 3 && m.length > 0 && (m === s || m.endsWith(` ${s}`) || m.endsWith(s));
}

/**
 * The city's side of a device, in words, from the engine's "filing" promise.
 * The engine counts filings for the whole job, so this only speaks for one
 * device when the answer is the same for all of them.
 */
export function deviceCityWords(filing: JobPromise | undefined, d: CalmDevice): string | null {
    if (!filing || filing.state === "not_applicable") return null;
    if (d.tally === "incomplete") return "City: can't file yet";
    if (d.group === "look" && d.tone !== "fail") return "City: nothing to file";
    if (d.group === "todo") return null;
    if (filing.state === "kept") return "City: filed";
    if (/^0 of /.test(filing.detail) || /once the report/i.test(filing.detail)) return "City: not filed";
    return null;
}

/** The folded pass group's city word, when one word holds for all of it. */
export function groupCityWords(filing: JobPromise | undefined): string | null {
    if (!filing || filing.state === "not_applicable") return null;
    if (filing.state === "kept") return "city: filed";
    if (/^0 of /.test(filing.detail) || /once the report/i.test(filing.detail)) return "city: not filed";
    return null;
}

// ── Files ───────────────────────────────────────────────────────────────────

export interface OfficeFile extends ReportAttachment {
    /** Which list it came from; decides which actions it has. */
    source: "document" | "closeout_upload" | "work_order_uploaded";
}

/**
 * Files = what the office put on the job, minus photos. Generated reports and
 * the cheat sheet live in City filing; tech photos live on their devices.
 */
export function officeFiles(lists: {
    documents: ReportAttachment[];
    closeoutUploads: ReportAttachment[];
    uploadedWorkOrders: ReportAttachment[];
}): OfficeFile[] {
    return [
        ...lists.documents.map((a) => ({ ...a, source: "document" as const })),
        ...lists.closeoutUploads.map((a) => ({ ...a, source: "closeout_upload" as const })),
        ...lists.uploadedWorkOrders.map((a) => ({ ...a, source: "work_order_uploaded" as const })),
    ]
        .filter((a) => !isImageFile(a))
        .sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
}

// ── Customer delivery ───────────────────────────────────────────────────────

/** One address's delivery in a word: sent / delivered / bounced / blocked. */
export function deliveryWord(r: Pick<SendHistoryRecipient, "status" | "reason" | "openedAt">): {
    word: string;
    bad: boolean;
} {
    // Sunny Ridge, Oct 2026: a size refusal read as "failed"/"bounced".
    if ((r.status === "undelivered" || r.status === "failed") && lower(r.reason).includes("too big")) {
        return { word: "too big for their email", bad: true };
    }
    if (r.status === "undelivered") {
        const why = lower(r.reason);
        if (why.includes("block")) return { word: "blocked", bad: true };
        if (why.includes("bounce")) return { word: "bounced", bad: true };
        return { word: "didn't arrive", bad: true };
    }
    if (r.status === "failed") return { word: "failed", bad: true };
    if (r.status === "dropped") return { word: "sent nothing", bad: true };
    if (r.openedAt) return { word: "opened", bad: false };
    return { word: r.status === "delivered" ? "delivered" : "sent", bad: false };
}

// ── Conversation ────────────────────────────────────────────────────────────

/** "Sep 28", or "Today". */
export function dayLabel(iso: string, now: Date = new Date()): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    if (d.toDateString() === now.toDateString()) return "Today";
    const sameYear = d.getFullYear() === now.getFullYear();
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric", ...(sameYear ? {} : { year: "numeric" }) });
}

export type FeedLine = { kind: "entry"; entry: FeedEntry } | { kind: "noise"; id: string; entries: FeedEntry[] };

export interface FeedDay {
    day: string;
    lines: FeedLine[];
}

/**
 * Newest-first entries into days, with every run of system entries folded
 * into one gray line ("3 system updates"). The story stays in sentences.
 */
export function groupFeedByDay(entries: FeedEntry[], now: Date = new Date()): FeedDay[] {
    const days: FeedDay[] = [];
    for (const e of entries) {
        const day = dayLabel(e.timestamp, now);
        let bucket = days[days.length - 1];
        if (!bucket || bucket.day !== day) {
            bucket = { day, lines: [] };
            days.push(bucket);
        }
        const last = bucket.lines[bucket.lines.length - 1];
        if (e.category === "system") {
            if (last?.kind === "noise") last.entries.push(e);
            else bucket.lines.push({ kind: "noise", id: `noise:${e.id}`, entries: [e] });
        } else {
            bucket.lines.push({ kind: "entry", entry: e });
        }
    }
    return days;
}

/** New since this person last opened the job. Never "new" on a first visit. */
export function isNewSince(iso: string, lastViewed: string | null): boolean {
    if (!lastViewed) return false;
    const t = new Date(iso).getTime();
    const v = new Date(lastViewed).getTime();
    return Number.isFinite(t) && Number.isFinite(v) && t > v;
}

// ── Money ───────────────────────────────────────────────────────────────────

export function money(n: number | string | null | undefined): string {
    const v = Number(n ?? 0);
    return `$${(Number.isFinite(v) ? v : 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/** An invoice's status in a word, from the facts on the row. */
export function invoiceWord(inv: {
    status: string;
    total_amount?: number;
    payments_total?: number | string | null;
    st_paid_on?: string | null;
    emailed_count?: number;
    undelivered_count?: number;
}): { word: string; tone: "pass" | "warn" | "fail" | "todo" } {
    const s = lower(inv.status);
    if (s === "paid" || inv.st_paid_on) return { word: "Paid", tone: "pass" };
    if (s === "void" || s === "voided" || s === "cancelled") return { word: "Void", tone: "todo" };
    const paid = Number(inv.payments_total ?? 0);
    if (paid > 0 && paid < Number(inv.total_amount ?? 0)) return { word: `Part paid`, tone: "warn" };
    if ((inv.undelivered_count ?? 0) > 0) return { word: "Didn't arrive", tone: "fail" };
    if ((inv.emailed_count ?? 0) > 0) return { word: "Sent", tone: "todo" };
    if (s === "draft") return { word: "Draft", tone: "warn" };
    return { word: "Not sent", tone: "warn" };
}
